import $ from 'jquery';
import actions from '@wc-stripe/actions';
import {getSetting, getFieldValue, setFieldValue, isFieldRequired} from '@wc-stripe/utils';

/**
 * Base Gateway Interface
 *
 * Defines the minimal contract that all payment gateways must implement.
 * Each gateway handles payment-method-specific logic (Link, Apple Pay, Google Pay, etc.)
 *
 * @since 4.0.0
 */
class BaseGateway {

    id;

    stripe = null;

    elements = null;

    elementType = '';

    paymentElement = null;

    _styles = {};

    controller = null;

    capabilities = [];

    _elementsSubmitted = false;

    _terms = [
        'applePay',
        'auBecsDebit',
        'bancontact',
        'card',
        'cashapp',
        'googlePay',
        'ideal',
        'paypal',
        'sepaDebit',
        'sofort',
        'usBankAccount'
    ];

    constructor({id, settings, context}) {
        this.id = id;
        this.actions = actions;
        this.settings = settings;
        this.context = context;
        if (this.settings.button_placement === 'payment_method') {
            this.capabilities.push('place_order_button');
        }
    }

    initialize() {
        this.actions.addAction('stripe_sdk_loaded', this.onStripeSDKLoaded.bind(this));
        this.actions.addAction('checkout_field_changed', this.onCheckoutFieldChanged.bind(this));
    }

    onCheckoutFieldChanged(element) {
        /**
         * We don't update the payment element if it's the expressCheckout because this will trigger an error.
         * "IntegrationError: billingAddressRequired, emailRequired, phoneNumberRequired, shippingAddressRequired
         * cannot be updated while the payment sheet is open."
         *
         * However, the payment sheet isn't open so this should be reported to Stripe as a bug.
         */
        if (this.elementType === 'expressCheckout') {
            return;
        }
        this.updatePaymentElement();
    }

    onStripeSDKLoaded(stripe) {
        this.setStripe(stripe);
    }

    createPaymentElement() {
        const options = this.actions.applyFilters('payment_element_options', this.buildPaymentElementOptions(), this);
        this.paymentElement = this.elements.create(this.elementType, options);
    }

    createElements() {
        if (this.stripe) {
            this.elements = this.stripe.elements(this.getElementOptions());
        }
    }

    updateElements() {
        if (this.elements) {
            this.elements.update(this.getElementOptions(true));
        }
    }

    updatePaymentElement() {
        if (this.paymentElement) {
            this.paymentElement.update(this.buildPaymentElementOptions(true));
        }
    }

    getOption(key, defaultValue = null) {
        return this.settings[key] ?? defaultValue;
    }

    getElementOptions(isUpdate = false) {
        let options = {
            locale: 'auto',
            currency: this.controller.getCurrency().toLowerCase(),
            ...this.settings.elementOptions
        };
        let amount = this.controller.getTotalCents();
        let subtotal = this.controller.getSubtotalCents();
        if (amount <= 0 && options.mode !== 'subscription') {
            if (this.context.isProduct() || this.context.isCart() || this.context.isMinicart()) {
                options.mode = 'setup';
            } else if (subtotal > 0) {
                amount = subtotal;
            } else {
                amount = 100;
            }
        }
        if (['payment', 'subscription'].includes(options.mode)) {
            options.amount = amount;
        }

        return this.actions.applyFilters('stripe_element_options', options, this);
    }

    /**
     * Set the controller instance after gateway creation
     * @param {BaseController} controller - The controller instance
     */
    setController(controller) {
        this.controller = controller;
    }

    /**
     * Set the Stripe.js instance (shared across all gateways)
     * @param {Object} stripe - The Stripe.js instance
     */
    setStripe(stripe) {
        this.stripe = stripe;
    }

    /**
     * Called by the controller once the payment method is confirmed available in the cart.
     * Ensures the Stripe elements instance is created or updated before canMakePayment() runs.
     * Subclasses can override to perform additional setup at this point.
     */
    onPaymentMethodAvailable() {
        if (!this.elements) {
            this.createElements();
        } else {
            this.updateElements();
        }
        if (!this.paymentElement) {
            this.createPaymentElement();
        }
    }

    /**
     * Check if the payment method can make payments
     * (Gateway-specific eligibility check before attempting to create payment elements)
     *
     * @returns {Promise<boolean>} True if the payment method is available and ready
     */
    async canMakePayment() {
        return this.isSetupMode() || this.isSubscriptionMode() || this.controller.getTotalCents() > 0;
    }

    /**
     * Check if payment elements should be created in the container
     * (Checks if elements already exist to prevent duplicates)
     *
     * @param {HTMLElement|NodeList} container - The container to check
     * @returns {boolean} True if elements should be created
     */
    shouldMountElement(container) {
        return !container?.querySelector('.StripeElement');
    }

    /**
     *
     * @returns {boolean}
     */
    shouldCreatePaymentMethod() {
        return false;
    }

    async createPaymentMethod() {
        try {
            const response = await this.stripe.createPaymentMethod({
                elements: this.elements,
                params: {
                    billing_details: {
                        ...(this.context.isCheckout() && {
                            address: this.getBillingAddress(),
                        }),
                        ...(this.hasDOMNameValue() && {
                            name: this.getBillingName()
                        }),
                        ...(this.getBillingEmail()
                            ? {email: this.getBillingEmail()}
                            : (this._paymentElementOptions?.fields?.billingDetails?.email === 'never' && {email: ''})),
                        ...(this.getBillingPhone()
                            ? {phone: this.getBillingPhone()}
                            : (this._paymentElementOptions?.fields?.billingDetails?.phone === 'never' && {phone: ''}))
                    }
                }
            });
            if (response.error) {
                throw response.error;
            }
            this.setPaymentMethodId(response.paymentMethod.id);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Mount payment element(s) in the specified container
     * (Could be buttons, fields, or other payment UI elements)
     *
     * @param {HTMLElement|NodeList} container - The container element(s) to render payment UI
     * @returns {Promise<Object>} Promise that resolves with payment element instances
     */
    async mountPaymentElement(container) {
        throw new Error('Must implement mountPaymentElement()');
    }

    /**
     * Validate payment element before form submission
     * Override in subclasses for gateway-specific validation (e.g., credit card fields)
     * @throws {Error} If validation fails with specific error message
     */
    async validatePaymentElement() {
        try {
            let response = await this.elements.submit();
            if (response.error) {
                throw response.error;
            }
            this._elementsSubmitted = true;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get the field key for storing payment intent ID
     * @returns {string} Payment intent field key
     */
    getPaymentIntentFieldKey() {
        return `${this.id}_payment_intent_id`;
    }

    setPaymentMethodId(value) {
        const key = `${this.id}_payment_method_id`;
        setFieldValue(key, value);
        // deprecated key used before version 4.0.0
        setFieldValue(`${this.id}_token_key`, value);
    }

    getPaymentMethodId() {
        return getFieldValue(`${this.id}_payment_method_id`);
    }

    /**
     * Returns true if the gateway has a payment method ID from the createPaymentMethod process.
     * @returns {boolean}
     */
    hasPaymentMethodId() {
        return !!this.getPaymentMethodId();
    }

    setSetupIntentId(value) {
        setFieldValue(`${this.id}_setup_intent_id`, value);
    }

    hasSetupIntentId() {
        const value = getFieldValue(`${this.id}_setup_intent_id`);
        return !!value;
    }

    clearElementSubmitted() {
        this._elementsSubmitted = false;
    }

    /**
     * Enable payment element(s)
     * Override in subclasses for gateway-specific implementation
     */
    enablePaymentElement() {
        // Override in subclasses
    }

    /**
     * Disable payment element(s)
     * Override in subclasses for gateway-specific implementation
     */
    disablePaymentElement() {
        // Override in subclasses
    }

    /**
     * Destroy payment element(s) and clean up resources
     * Override in subclasses for gateway-specific implementation
     */
    destroyPaymentElement() {
        // Override in subclasses
    }

    /**
     * Check if gateway supports a specific capability
     * @param {string} capability - The capability to check (e.g., 'place_order_button', 'tokenization')
     * @returns {boolean} True if gateway supports the capability
     */
    supports(capability) {
        return this.capabilities.includes(capability);
    }

    /**
     * Returns true if a requires_action status means the customer cancelled (e.g. closed 3DS modal).
     * Override in subclasses where requires_action can mean pending verification instead.
     *
     * @param {Object} result - The confirmation result from Stripe
     * @returns {boolean}
     */
    isPaymentCancelled(result) {
        const nextAction = result.paymentIntent?.next_action;
        if (nextAction?.type === 'verify_with_microdeposits') {
            return false;
        }
        return true;
    }

    /**
     * Return true if the mode is 'payment'.
     * @returns {boolean}
     */
    isPaymentMode() {
        return this.settings.elementOptions.mode === 'payment';
    }

    isSubscriptionMode() {
        return this.settings.elementOptions.mode === 'subscription';
    }

    isSetupMode() {
        return this.settings.elementOptions.mode === 'setup';
    }

    getPaymentElementStyles() {
        return Object.entries(this._styles)
            .map(([key, value]) => `${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}: ${value}`)
            .join('; ');
    }

    setSavePaymentMethod(value) {
        if (value) {
            this.settings.elementOptions.mode = 'setup';
        } else {
            this.settings.elementOptions.mode = 'payment';
        }
    }

    buildPaymentElementOptions(isUpdate = false) {
        this._paymentElementOptions = {
            fields: {
                billingDetails: !this.context.isCheckout() ? 'auto' : {
                    address: 'never',
                    name: this.hasDOMNameValue() ? 'never' : 'auto',
                    email: (this.getBillingEmail() || isFieldRequired('billing_email')) ? 'never' : 'auto',
                    phone: (this.getBillingPhone() || isFieldRequired('billing_phone')) ? 'never' : 'auto',
                }
            },
            ...(!isUpdate && {
                wallets: {
                    applePay: 'never',
                    googlePay: 'never',
                    link: this.getPaymentMethodTypes().includes('link') ? 'auto' : 'never'
                },
            }),
            defaultValues: {
                billingDetails: {
                    name: this.getBillingName(),
                    email: this.getBillingEmail(),
                    phone: this.getBillingPhone()
                }
            },
            terms: this.getTerms(),
            ...this.settings.paymentElementOptions
        };
        return this._paymentElementOptions;
    }

    getPaymentMethodTypes() {
        if (this.settings.elementOptions?.paymentMethodTypes) {
            return this.settings.elementOptions.paymentMethodTypes;
        }
        return [this.settings.paymentMethodType];
    }

    getTerms() {
        const rule = this.settings.termsDisplayRule;
        return this._terms.reduce((terms, key) => {
            terms[key] = rule;
            return terms;
        }, {});
    }

    async confirmPayment(data, options = {}) {
        return await this.stripe.confirmPayment({
            ...(!options.skipElements && {
                elements: this.elements
            }),
            clientSecret: data.client_secret,
            redirect: 'if_required',
            confirmParams: {
                return_url: data.return_url,
                payment_method_data: {
                    billing_details: data.billing_details
                },
                ...(data.confirmation_args && data.confirmation_args)
            }
        });
    }

    async confirmSetup(data, options = {}) {
        return await this.stripe.confirmSetup({
            ...(!options.skipElements && {
                elements: this.elements
            }),
            clientSecret: data.client_secret,
            redirect: 'if_required',
            confirmParams: {
                ...(data.return_url && {
                    return_url: data.return_url,
                }),
                payment_method_data: {
                    ...(data.billing_details && {
                        billing_details: data.billing_details
                    })
                },
                ...(data.confirmation_args && data.confirmation_args)
            }
        });
    }

    getBillingName() {
        if (this.context.isAddPaymentMethod()) {
            const customer = getSetting('customer');
            return customer.billing_address.name;
        }
        if (this.context.isOrderPay()) {
            return this.controller.order.getBillingName();
        }
        const $billingFirst = $('[name="billing_first_name"]');
        const $billingLast = $('[name="billing_last_name"]');
        const $shippingFirst = $('[name="shipping_first_name"]');
        const $shippingLast = $('[name="shipping_last_name"]');

        // Priority 1: Visible billing fields
        if ($billingFirst.is(':visible')) {
            return [$billingFirst.val() || '', $billingLast.val() || ''].filter(Boolean).join(' ');
        }

        // Priority 2: Visible shipping fields
        if ($shippingFirst.is(':visible')) {
            return [$shippingFirst.val() || '', $shippingLast.val() || ''].filter(Boolean).join(' ');
        }

        // Priority 3: Hidden billing fields (multi-step checkout)
        if ($billingFirst.val()) {
            return [$billingFirst.val() || '', $billingLast.val() || ''].filter(Boolean).join(' ');
        }

        // Priority 4: Hidden shipping fields (multi-step checkout)
        if ($shippingFirst.val()) {
            return [$shippingFirst.val() || '', $shippingLast.val() || ''].filter(Boolean).join(' ');
        }

        return '';
    }

    getBillingEmail() {
        if (this.context.isAddPaymentMethod()) {
            const customer = getSetting('customer');
            return customer?.billing_address?.email || '';
        }
        if (this.context.isOrderPay()) {
            return this.controller.order.getBillingEmail();
        }
        const $billingEmail = $('[name="billing_email"]');

        // Priority 1: Visible billing email field
        if ($billingEmail.is(':visible')) {
            return $billingEmail.val() || '';
        }

        // Priority 2: Hidden billing email field (multi-step checkout)
        if ($billingEmail.val()) {
            return $billingEmail.val();
        }

        return '';
    }

    getBillingPhone() {
        if (this.context.isAddPaymentMethod()) {
            const customer = getSetting('customer');
            return customer?.billing_address?.phone || '';
        }
        if (this.context.isOrderPay()) {
            return this.controller.order.getBillingPhone();
        }
        const $billingPhone = $('[name="billing_phone"]');
        const $shippingPhone = $('[name="shipping_phone"]');

        // Priority 1: Visible billing phone field
        if ($billingPhone.is(':visible')) {
            return $billingPhone.val() || '';
        }

        // Priority 2: Visible shipping phone field
        if ($shippingPhone.is(':visible')) {
            return $shippingPhone.val() || '';
        }

        // Priority 3: Hidden billing phone field (multi-step checkout)
        if ($billingPhone.val()) {
            return $billingPhone.val();
        }

        // Priority 4: Hidden shipping phone field (multi-step checkout)
        if ($shippingPhone.val()) {
            return $shippingPhone.val();
        }

        return '';
    }

    getBillingAddress() {
        if (this.context.isAddPaymentMethod()) {
            const customer = getSetting('customer');
            return this.transformAddress(customer?.billing_address);
        }
        if (this.context.isOrderPay()) {
            return this.transformAddress(this.controller.order.getBillingAddress());
        }
        const $billingCountry = $('[name="billing_country"]');
        const $shippingCountry = $('[name="shipping_country"]');

        // Priority 1: Visible billing address fields
        if ($billingCountry.is(':visible')) {
            return {
                line1: $('[name="billing_address_1"]').val() || '',
                line2: $('[name="billing_address_2"]').val() || '',
                postal_code: $('[name="billing_postcode"]').val() || '',
                city: $('[name="billing_city"]').val() || '',
                state: $('[name="billing_state"]').val() || '',
                country: $billingCountry.val() || ''
            };
        }

        // Priority 2: Visible shipping address fields
        if ($shippingCountry.is(':visible')) {
            return {
                line1: $('[name="shipping_address_1"]').val() || '',
                line2: $('[name="shipping_address_2"]').val() || '',
                postal_code: $('[name="shipping_postcode"]').val() || '',
                city: $('[name="shipping_city"]').val() || '',
                state: $('[name="shipping_state"]').val() || '',
                country: $shippingCountry.val() || ''
            };
        }

        // Priority 3: Hidden billing address fields (multi-step checkout)
        if ($billingCountry.val()) {
            return {
                line1: $('[name="billing_address_1"]').val() || '',
                line2: $('[name="billing_address_2"]').val() || '',
                postal_code: $('[name="billing_postcode"]').val() || '',
                city: $('[name="billing_city"]').val() || '',
                state: $('[name="billing_state"]').val() || '',
                country: $billingCountry.val()
            };
        }

        // Priority 4: Hidden shipping address fields (multi-step checkout)
        if ($shippingCountry.val()) {
            return {
                line1: $('[name="shipping_address_1"]').val() || '',
                line2: $('[name="shipping_address_2"]').val() || '',
                postal_code: $('[name="shipping_postcode"]').val() || '',
                city: $('[name="shipping_city"]').val() || '',
                state: $('[name="shipping_state"]').val() || '',
                country: $shippingCountry.val()
            };
        }

        return {
            line1: '',
            line2: '',
            postal_code: '',
            city: '',
            state: '',
            country: ''
        };
    }

    getBillingDetails() {
        return {
            name: this.getBillingName(),
            phone: this.getBillingPhone(),
            email: this.getBillingEmail(),
            address: this.getBillingAddress()
        };
    }

    /**
     * Returns true if a name value exists in the DOM (visible or hidden field).
     * Used to determine if we should provide the name to Stripe or let Stripe render the field.
     *
     * @returns {boolean}
     */
    hasDOMNameValue() {
        // Priority 1: Visible billing name field
        if ($('[name="billing_first_name"]').is(':visible')) {
            return true;
        }
        // Priority 2: Visible shipping name field
        if ($('[name="shipping_first_name"]').is(':visible')) {
            return true;
        }
        // Priority 3: Hidden fields with value (multi-step checkout)
        if (this.getBillingName()) {
            return true;
        }
        return false;
    }

    /**
     * Returns true if an email value exists in the DOM (visible or hidden field).
     * Used to determine if we should provide the email to Stripe or let Stripe render the field.
     *
     * @returns {boolean}
     */
    hasDOMEmailValue() {
        // Priority 1: Visible billing email field
        if ($('[name="billing_email"]').is(':visible')) {
            return true;
        }
        // Priority 2: Hidden field with value (multi-step checkout)
        if (this.getBillingEmail()) {
            return true;
        }
        return false;
    }

    /**
     * Returns true if a phone value exists in the DOM (visible or hidden field).
     * Used to determine if we should provide the phone to Stripe or let Stripe render the field.
     *
     * @returns {boolean}
     */
    hasDOMPhoneValue() {
        // Priority 1: Visible billing phone field
        if ($('[name="billing_phone"]').is(':visible')) {
            return true;
        }
        // Priority 2: Visible shipping phone field
        if ($('[name="shipping_phone"]').is(':visible')) {
            return true;
        }
        // Priority 3: Hidden fields with value (multi-step checkout)
        if (this.getBillingPhone()) {
            return true;
        }
        return false;
    }

    /**
     * Transforms a WooCommerce formatted address to a Stripe formatted address
     * @param address
     */
    transformAddress(address) {
        return {
            line1: address.address_1 || '',
            line2: address.address_2 || '',
            city: address.city || '',
            state: address.state || '',
            postal_code: address.postcode || '',
            country: address.country || ''
        }
    }
}

export {BaseGateway};
export default BaseGateway;